/**
 * LocalStorage Context Hook
 * 
 * Provides a React context for managing localStorage with automatic hydration and sync.
 * 
 * @example
 * // In a component:
 * const { getItem, setItem, removeItem, clear } = useLocalStorage();
 * 
 * // Set a value (automatically saves to localStorage)
 * setItem('userPreferences', { theme: 'dark', language: 'es' });
 * 
 * // Get a value (with type safety)
 * const preferences = getItem<{ theme: string; language: string }>('userPreferences');
 * 
 * // Remove a key
 * removeItem('userPreferences');
 * 
 * // Clear all storage
 * clear();
 * 
 * // Get all items
 * const allItems = getAllItems();
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from 'react';

interface LocalStorageContextType {
  getItem: <T>(key: string) => T | null;
  setItem: <T>(key: string, value: T) => void;
  removeItem: (key: string) => void;
  clear: () => void;
  getAllItems: () => Record<string, unknown>;
}

const LocalStorageContext = createContext<LocalStorageContextType | undefined>(
  undefined
);

// Helper function to check if we're in browser environment
const isBrowser = () => typeof window !== 'undefined';

interface LocalStorageProviderProps {
  children: ReactNode;
  storageKey?: string; // Optional prefix for namespacing
}

export const LocalStorageProvider = ({
  children,
  storageKey = 'app-storage',
}: LocalStorageProviderProps) => {
  const [storage, setStorage] = useState<Record<string, unknown>>({});
  const [isHydrated, setIsHydrated] = useState(false);

  // Hydrate state from localStorage on mount
  useEffect(() => {
    if (!isBrowser()) return;

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        setStorage(parsed);
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    } finally {
      setIsHydrated(true);
    }
  }, [storageKey]);

  // Save to localStorage whenever storage changes (after hydration)
  useEffect(() => {
    if (!isBrowser() || !isHydrated) return;

    try {
      localStorage.setItem(storageKey, JSON.stringify(storage));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [storage, storageKey, isHydrated]);

  const getItem = useCallback(<T,>(key: string): T | null => {
    const value = storage[key];
    return value !== undefined ? (value as T) : null;
  }, [storage]);

  const setItem = useCallback(<T,>(key: string, value: T): void => {
    setStorage(prev => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const removeItem = useCallback((key: string): void => {
    setStorage(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  }, []);

  const clear = useCallback((): void => {
    setStorage({});
    if (isBrowser()) {
      try {
        localStorage.removeItem(storageKey);
      } catch (error) {
        console.error('Error clearing localStorage:', error);
      }
    }
  }, [storageKey]);

  const getAllItems = useCallback((): Record<string, unknown> => {
    return { ...storage };
  }, [storage]);

  const value: LocalStorageContextType = {
    getItem,
    setItem,
    removeItem,
    clear,
    getAllItems,
  };

  return (
    <LocalStorageContext.Provider value={value}>
      {children}
    </LocalStorageContext.Provider>
  );
};

export const useLocalStorage = (): LocalStorageContextType => {
  const context = useContext(LocalStorageContext);
  if (context === undefined) {
    throw new Error(
      'useLocalStorage must be used within a LocalStorageProvider'
    );
  }
  return context;
};
